<template>
  <vf-input v-bind="{ disabled, invalid, variant }" :required @input="onInput">
    <slot />
    <template #input>
      <base-input
        ref="inputRef"
        v-bind="{ disabled, placeholder, required }"
        v-model="maskedValue"
        @change="onChange"
      />
    </template>
  </vf-input>
</template>

<script lang="ts" setup>
/**
 * This component expects the model value to be a string in any format.
 * It will emit the value transformed with a mask if provided via prop.
 * It will not provide any extra validation, just the input value formatting.
 */
const props = defineProps<{
  mask: string
  modelValue: string
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  variant?: 'inverse'
  required?: boolean
  transforms?: string[]
}>()

const emit = defineEmits<{
  'update:modelValue': [payload: string]
  'change': [value: string]
}>()

const transformers = {
  capitalize: (value: string) => value.toUpperCase()
}

const maskFormat = computed(() => props.mask ? props.mask.replace(/#/g, 'X').replace(/A/g, 'X') : '')

const { value: maskedValue, el: inputRef } = useMask(maskFormat.value)

onMounted(() => {
  // set initial value to trigger mask formatting
  maskedValue.value = props.modelValue
})

// Temporary solution to emit 'change' event
// 'update:modelValue' should be emitted on 'input' event
const onChange = () => {
  emit('update:modelValue', maskedValue.value)
  emit('change', maskedValue.value)
}

/**
 * Passes value through transformer functions, configured in SFCC.
 * Example: 'capitalize' transformer used for CA postal codes (e.g. e5a 3n3 -> E5A 3N3).
 */
const onInput = (event) => {
  if (!event.target.value) return
  let newValue = event.target.value
  for (const transformer of props.transforms || []) {
    if (!transformers[transformer]) continue
    newValue = transformers[transformer](newValue)
  }
  maskedValue.value = newValue
}
</script>
